<template>
    <div class="w-full flex-auto flex flex-col p-8 bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full w-full container mx-auto">
            <div class="h-full flex flex-col bg-white rounded-2xl shadow-lg p-8">
                <!-- Header -->
                <div class="mb-6">
                    <h1 class="text-2xl font-bold text-gray-800">🚀 Innovation Project Details</h1>
                    <p class="text-sm text-gray-500">Submitted on {{ formatDate(item.created_at) }}</p>

                    <!-- Submitter Info -->
                    <div v-if="item.user" class="mt-3 bg-gray-50 border rounded-lg p-4 text-sm">
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700 mr-1">Submitted by:</span>{{ item.user.name }}
                        </p>
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700">Email:</span>
                            <a :href="`mailto:${item.user.email}`" class="ml-1 text-blue-600 hover:underline">{{
                                item.user.email }}</a>
                        </p>
                        <p v-if="item.user.phone_number" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Phone:</span>
                            <a :href="`tel:${item.user.phone_number}`" class="ml-1 hover:underline">{{
                                item.user.phone_number
                            }}</a>
                        </p>
                        <p v-if="item.user.entity" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ primaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ primaryEntity(item.user.entity) }}</span>
                        </p>
                        <p v-if="item.user.entity && secondaryEntities(item.user.entity).length" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ secondaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ secondaryEntities(item.user.entity).join(', ') }}</span>
                        </p>
                        <p v-if="item.user.campuses" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Campus:</span>
                            <span class="ml-1">{{ item.user.campuses.split(',')[0] }}</span>
                        </p>
                    </div>
                </div>

                <!-- Project Title -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">📝 Project Title</h2>
                    <p class="text-gray-800 leading-relaxed break-all">{{ item.project_title }}</p>
                </div>

                <!-- Project Type -->
                <div v-if="item.project_type_code" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">🏷️ Project Type</h2>
                    <p class="text-gray-800 leading-relaxed">{{ formatProjectType(item.project_type_code) }}</p>
                </div>

                <!-- Project Category -->
                <div v-if="item.project_type" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">📂 Project Category</h2>
                    <p class="text-gray-800 leading-relaxed">{{ formatProjectCategory(item.project_type) }}</p>
                </div>

                <!-- Company Funding Info -->
                <div v-if="item.is_company_funded" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">💰 Funding Information</h2>
                    <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p class="text-gray-800 leading-relaxed mb-2">
                            <span class="font-semibold">Status:</span> Company Funded
                        </p>
                        <p v-if="item.company_name" class="text-gray-800 leading-relaxed">
                            <span class="font-semibold">Company Name:</span> {{ item.company_name }}
                        </p>
                    </div>
                </div>

                <!-- Description -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">🧠 Project Description</h2>
                    <p class="text-gray-800 leading-relaxed break-word">{{ item.description }}</p>
                </div>


                <!-- Attachments -->
                <div v-if="item.attachments && item.attachments.length" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">📎 Uploaded Files</h2>
                    <ul class="list-disc list-inside space-y-2">
                        <li v-for="(file, index) in item.attachments" :key="index">
                            <a :href="getUrl(file.file_path)" target="_blank" class="text-blue-600 hover:underline">
                                {{ file.label || file.file_name || file.original_filename || 'Document' }}
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Back Button -->
                <div class="mt-8 flex-auto flex flex-col justify-end items-end">
                    <div @click.stop="$emit('close')"
                        class="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition cursor-pointer">
                        ← Back to List
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ["item"],
    methods: {
        primaryEntity(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length ? this.mapAths(list[0]) : '';
        },
        primaryLabel(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length <= 1 ? 'Entity' : 'Primary Entity';
        },
        secondaryEntities(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            if (list.length <= 1) return [];
            return list.slice(1).map(this.mapAths);
        },
        secondaryLabel(entityStr) {
            const count = this.secondaryEntities(entityStr).length;
            return count <= 1 ? 'Secondary Entity' : 'Secondary Entities';
        },
        mapAths(value) {
            return value === 'ATHS' ? 'ATS' : value;
        },
        formatProjectType(type) {
            if (!type) return '';
            // If type is already a full name, return it
            if (type.length > 2) {
                return type;
            }
            // Map short codes to names (fallback if project types not loaded)
            const typeMap = {
                'SC': 'Student Capstone',
                'SI': 'Student Independent',
                'FR': 'Faculty Research',
                'FI': 'Faculty Innovation'
            };
            return typeMap[type] || type;
        },
        formatProjectCategory(category) {
            if (!category) return '';
            const categoryMap = {
                'individual': 'Individual Project',
                'group': 'Group Project'
            };
            return categoryMap[category] || category;
        },
        formatDate(dateStr) {
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        getUrl(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
    }
}
</script>
